import React from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { formatFeedbackText } from '../helpers/textFormatter'
import type { DraftJsContent } from 'pure-js/libs/Common'
import { draftJsContentToHtml, isDraftJsContent } from '../helpers/draftJsToHtml'

export interface FeedbackTextCardProps {
  /**
   * Title for the card (e.g., "What's working" or "Watch out")
   */
  title: string
  /**
   * Content text (may contain markdown-like formatting: **bold**, \n\n)
   */
  content: string | DraftJsContent
  /**
   * Variant affects styling
   * - positive: For "What's working" (default)
   * - warning: For "Watch out"
   */
  variant?: 'positive' | 'warning'
  /**
   * Design variant affects box styling
   * - standard: Uses standard box styling matching EmotionStateSection (gradient background, white08 border, 16px radius, blur)
   * - legacy: Keeps current design (no background/border on card itself, relies on LatestFeedbackLayoutBox)
   */
  designVariant?: 'standard' | 'legacy'
  /**
   * Optional custom styling
   */
  sx?: BoxProps['sx']
}

type BoxProps = React.ComponentProps<typeof Box>

/**
 * FeedbackTextCard - Displays formatted feedback text with title
 *
 * Features:
 * - Supports markdown-like formatting (**bold**, \n\n for paragraphs)
 * - Two variants: positive (default) and warning
 * - Uses design system colors and typography
 * - Responsive layout
 *
 * @example
 * ```tsx
 * <FeedbackTextCard
 *   title="What's working"
 *   content="As a team player, you **actively contribute** to the conversation."
 *   variant="positive"
 * />
 * ```
 */
export const FeedbackTextCard: React.FC<FeedbackTextCardProps> = ({
  title,
  content,
  variant: _variant = 'positive', // Reserved for future styling differences
  designVariant = 'legacy',
  sx
}) => {
  const formattedContent = React.useMemo(() => {
    if (isDraftJsContent(content)) {
      return draftJsContentToHtml(content)
    }
    return formatFeedbackText(content)
  }, [content])

  // Standard box styling - when designVariant is 'standard', box styling is on LatestFeedbackLayoutBox parent
  // So FeedbackTextCard should just be a content container without box styling
  const standardBoxStyles = {
    padding: 0
  }

  // Legacy box styling (minimal, relies on parent)
  const legacyBoxStyles = {
    padding: '30px',
    borderRadius: '10px'
  }

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        width: '100%',
        ...(designVariant === 'standard' ? standardBoxStyles : legacyBoxStyles),
        ...sx
      }}
    >
      {/* Title - Gradient text matching CSS spec */}
      <Typography
        sx={{
          boxSizing: 'border-box',
          width: { xs: '100%', sm: '100%', lg: '100%' },
          fontFamily: 'Lexend Deca',
          fontStyle: 'normal',
          fontWeight: 600,
          fontSize: { xs: '20px', sm: '24px', md: '26px' },
          lineHeight: { xs: '28px', sm: '32px', md: '34px' },
          background: 'linear-gradient(360deg, #FFD196 -15.91%, #FFAB40 1.14%, #FFFFFF 56.35%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textFillColor: 'transparent',
          textShadow: '0px 0px 10px rgba(0, 0, 0, 0.5), 0px 0px 20px rgba(255, 171, 64, 0.4)',
          flex: 'none',
          order: 0,
          flexGrow: 0,
          margin: 0
        }}
      >
        {title}
      </Typography>

      {/* Content with formatted HTML - matching CSS spec */}
      <Typography
        component="div"
        sx={{
          ...INTERAX_TYPOGRAPHY.body3,
          color: INTERAX_COLORS.typographyFont,
          lineHeight: 1.6,
          '& p': {
            marginBottom: '8px',
            '&:last-child': {
              marginBottom: 0
            }
          },
          '& ol, & ul': {
            marginLeft: '20px',
            marginBottom: '8px',
            '& li': {
              marginBottom: '4px'
            }
          },
          '& strong': {
            fontWeight: 600
          }
        }}
        dangerouslySetInnerHTML={{ __html: formattedContent }}
      />
    </Box>
  )
}

export default FeedbackTextCard
